/**
 * Pfade: Baukasten, glatte Kurven durch Punkte und Grundformen.
 */
import { clamp, fmt } from './geometry.ts';
import type { Point } from './geometry.ts';

/** Pfad aus Bausteinen. */
export class PathBuilder {
  private readonly parts: string[] = [];

  move(x: number, y: number): this {
    this.parts.push(`M${fmt(x)} ${fmt(y)}`);
    return this;
  }

  line(x: number, y: number): this {
    this.parts.push(`L${fmt(x)} ${fmt(y)}`);
    return this;
  }

  quad(cx: number, cy: number, x: number, y: number): this {
    this.parts.push(`Q${fmt(cx)} ${fmt(cy)} ${fmt(x)} ${fmt(y)}`);
    return this;
  }

  curve(c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number): this {
    this.parts.push(`C${fmt(c1x)} ${fmt(c1y)} ${fmt(c2x)} ${fmt(c2y)} ${fmt(x)} ${fmt(y)}`);
    return this;
  }

  arc(rx: number, ry: number, large: boolean, sweep: boolean, x: number, y: number): this {
    this.parts.push(`A${fmt(rx)} ${fmt(ry)} 0 ${large ? 1 : 0} ${sweep ? 1 : 0} ${fmt(x)} ${fmt(y)}`);
    return this;
  }

  close(): this {
    this.parts.push('Z');
    return this;
  }

  toString(): string {
    return this.parts.join('');
  }
}

/** Neuer Pfad. */
export function path(): PathBuilder {
  return new PathBuilder();
}

/**
 * Glatter Pfad durch Punkte (Catmull-Rom als kubische Bézierkurven).
 * @param closed Geschlossene Form statt offener Linie.
 */
export function smooth(points: readonly Point[], closed: boolean, tension = 1): string {
  const count = points.length;
  if (count < 2) {
    return '';
  }
  const at = (index: number): Point =>
    closed
      ? (points[((index % count) + count) % count] as Point)
      : (points[clamp(index, 0, count - 1)] as Point);
  const start = at(0);
  const builder = path().move(start.x, start.y);
  const segments = closed ? count : count - 1;
  const k = tension / 6;
  for (let index = 0; index < segments; index += 1) {
    const p0 = at(index - 1);
    const p1 = at(index);
    const p2 = at(index + 1);
    const p3 = at(index + 2);
    builder.curve(
      p1.x + (p2.x - p0.x) * k,
      p1.y + (p2.y - p0.y) * k,
      p2.x - (p3.x - p1.x) * k,
      p2.y - (p3.y - p1.y) * k,
      p2.x,
      p2.y,
    );
  }
  if (closed) {
    builder.close();
  }
  return builder.toString();
}

/**
 * Punkte auf einer Ellipse mit Verformung je Winkel. So entstehen organische
 * Umrisse wie Kartoffeln, Teig oder Fleisch.
 */
export function blobPoints(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  count: number,
  radiusFactor: (angle: number, index: number) => number = () => 1,
): Point[] {
  const points: Point[] = [];
  for (let index = 0; index < count; index += 1) {
    const angle = (index / count) * Math.PI * 2;
    const factor = radiusFactor(angle, index);
    points.push({ x: cx + Math.cos(angle) * rx * factor, y: cy + Math.sin(angle) * ry * factor });
  }
  return points;
}

/** Pfad einer Ellipse. */
export function ellipsePath(cx: number, cy: number, rx: number, ry: number): string {
  return path()
    .move(cx - rx, cy)
    .arc(rx, ry, false, true, cx + rx, cy)
    .arc(rx, ry, false, true, cx - rx, cy)
    .close()
    .toString();
}

/** Pfad eines Rechtecks mit abgerundeten Ecken. */
export function roundedRectPath(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  return path()
    .move(x + r, y)
    .line(x + width - r, y)
    .quad(x + width, y, x + width, y + r)
    .line(x + width, y + height - r)
    .quad(x + width, y + height, x + width - r, y + height)
    .line(x + r, y + height)
    .quad(x, y + height, x, y + height - r)
    .line(x, y + r)
    .quad(x, y, x + r, y)
    .close()
    .toString();
}

/** Offener Pfad durch eine Punktliste mit geraden Linien. */
export function polyline(points: readonly Point[], closed = false): string {
  const builder = path();
  points.forEach((point, index) => {
    if (index === 0) {
      builder.move(point.x, point.y);
    } else {
      builder.line(point.x, point.y);
    }
  });
  if (closed) {
    builder.close();
  }
  return builder.toString();
}
